import React from 'react';
import { Timestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { SingleMenuStepProps } from '../../components/UI-Elements/Flow/Steps/SingleStep/SingleMenuStep';
import { createBalanceFlow } from '../../sp-firebase/balanceFlowFB';
import { getBaseInstrumentsByIds, getInstrumentsByIds } from '../../sp-firebase/instrumentFB';
import {
  IBalanceFlow,
  IBalanceFlowProps,
  IBalanceInstruction,
  IBalanceStepDBData,
} from '../../sp-types/balanceFlowType';
import { IMaterialExposure } from '../../sp-types/netExposureType';
import { BalanceFlowCloser } from './BalanceFlowComponents/BalanceFlow-Closer/BalanceFlowCloser';
import { BalanceFlowInstructions } from './BalanceFlowComponents/BalanceFlow-Execution/BalanceFlow-instructions/BalanceFlowInstructions';
import { BalanceFlowExecutionReport } from './BalanceFlowComponents/BalanceFlow-Execution/BalancFlow-ExecutionReport/BalanceFlowExecutionReport';
import { BalanceFlowMethodSelection } from './BalanceFlowComponents/BalanceFlow-MethodSelection/BalanceFlowMethodSelection';
import { BalanceFlowPreferredRoute } from './BalanceFlowComponents/BalanceFlow-PreferredRoute/BalanceFlowPreferredRoute';
import { BalanceFlowTransactionApproval } from './BalanceFlowComponents/BalanceFlow-TransactionApproval/BalanceFlowTransactionApproval';
import { BalanceOverview } from './BalanceFlowComponents/BalanceOverview';
import { IFinancialPosition } from '../../sp-types/financialPositionType';
import { MonthYear } from '../../utils/MonthYear/monthYear';
import { MonthYearValue } from '../../utils/MonthYear/MonthYearValue';
import { IRoute } from '../../sp-types';
import { getInstrumentKey } from '../../sp-types/instrumentType';
import { generatePositionInstructions } from './BalanceBusinessLogic';
import { getDate, getMonthYear } from '../FinancialPositions/components/FinancialPositionsUtils';
import { Account } from '../../sp-types/accountTypes';
import { getRiskTolerance, createEmptyRiskTolerance } from '../../utils/accountUtils';
import { getBalanceMethodExposureLocation, BALANCE_METHOD_TO_IMAGE } from './BalanceFlowComponents/Utils';

const MAX_FUTURE_EXPOSURE = 12;

export const emptyBalanceFlowToAdd = (
  accountId: string,
  formulaId: string,
  currentExposure: number,
  materialExposure: IMaterialExposure,
  endDate: Date,
  mainCause?: string,
): IBalanceFlow => {
  const newId = uuidv4();
  const stepsList: IBalanceStepDBData[] = [
    { order: 0, status: 'Incomplete', stepName: 'balanceOverview' },
    { order: 1, status: 'Unavailable', stepName: 'methodSelection' },
    { order: 2, status: 'Unavailable', stepName: 'preferredRoutes' },
    { order: 3, status: 'Unavailable', stepName: 'transactionApproval' },
    { order: 4, status: 'Unavailable', stepName: 'transactionExecution' },
    { order: 5, status: 'Unavailable', stepName: 'executionReport' },
    { order: 6, status: 'Unavailable', stepName: 'closer' },
  ];

  return {
    exposureBeforeBalance: currentExposure,
    accountId: accountId,
    id: newId,
    balanceDate: Timestamp.now(),
    startDate: Timestamp.now(),
    dueDate: Timestamp.fromDate(endDate),
    formulaId,
    steps: stepsList,
    status: 'started',
    savedPositions: false,
    materialExposure: materialExposure,
    mainCause,
    isBalanceBlocked: false,
  } as IBalanceFlow;
};

export async function startBalanceFlowGetId(
  accountId: string,
  formulaId: string,
  currentExposure: number,
  totalAmounts: IMaterialExposure,
  mainCause?: string,
): Promise<string | null | undefined> {
  // TODO: temporary change - we should recalc the exposure only
  // const isBalanceFlowOpen = await checkIfOpenBalanceFlow(accountId, materialId);

  // if (isBalanceFlowOpen) {
  //   const openBalance = await getOpenBalanceFlow(accountId, materialId, ['underwriting', 'started']);
  //   // TODO: update total amounts?
  //   return openBalance?.id ?? null;
  // }
  const currDate = { month: new Date().getMonth() + 1, year: new Date().getFullYear() };

  const max = Math.max(
    ...totalAmounts.futureExpCalendar.map(({ date }) => date.year * 100 + date.month),
    currDate.year * 100 + currDate.month,
  );
  const maxDate = { year: max / 100, month: max % 100 };
  const emptyBalanceFlow = emptyBalanceFlowToAdd(
    accountId,
    formulaId,

    currentExposure,
    totalAmounts,
    new Date(maxDate.year, maxDate.month, 2),
    mainCause,
  );
  const createdObject = await createBalanceFlow(emptyBalanceFlow, emptyBalanceFlow.id);
  return createdObject?.id ?? null;
}

export async function balanceMaterial(
  material: string,
  accountId: string,
  netExposure: number,
  totalExposureData: IMaterialExposure,
  mainCause: string = 'New future orders added',
): Promise<string> {
  return (await startBalanceFlowGetId(accountId, material, netExposure, totalExposureData, mainCause)) || '';
}

export const getMenuStepsDataFromBalanceFlow = (
  balanceSteps: IBalanceStepDBData[],
  selectedStep: string,
  onClick: (_value: string) => void,
): SingleMenuStepProps[] => {
  const newItem: SingleMenuStepProps[] = balanceSteps
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((item) => ({
      id: alphabet[item.order],
      isSelected: selectedStep === item.stepName,
      stepName: item.stepName,
      stepStatus: item.status,
      onClick: () => onClick(item.stepName),
      toolTipText: balanceStepDictionary[item.stepName].toolTip,
      stepDisplayName: balanceStepDictionary[item.stepName].displayText,
    }));
  return newItem;
};

interface IStepsData {
  displayText: string;
  toolTip: string;
  Component: React.ComponentType<IBalanceFlowProps>;
}

export const balanceStepDictionary: { [key: string]: IStepsData } = {
  balanceOverview: {
    displayText: 'Balance overview',
    toolTip: 'Balance Overview',
    Component: BalanceOverview,
  },
  methodSelection: {
    displayText: 'Method selection',
    toolTip: 'Method selection',
    Component: BalanceFlowMethodSelection,
  },
  preferredRoutes: {
    displayText: 'Preferred route',
    toolTip: 'Preferred route',
    Component: BalanceFlowPreferredRoute,
  },
  transactionApproval: {
    displayText: 'Transaction approval',
    toolTip: 'Transaction approval',
    Component: BalanceFlowTransactionApproval,
  },
  transactionExecution: {
    displayText: 'Execution recommendations',
    toolTip: 'Execution recommendations',
    Component: BalanceFlowInstructions,
  },
  executionReport: {
    displayText: 'Execution report',
    toolTip: 'Execution report',
    Component: BalanceFlowExecutionReport,
  },
  closer: {
    displayText: 'Balance summary',
    toolTip: 'Balance summary',
    Component: BalanceFlowCloser,
  },
};

export const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export function updateBalanceStepStatus(
  balanceFlow: IBalanceFlow,
  stepIndex: number,
  newStatus: IBalanceStepDBData['status'],
): IBalanceFlow {
  const updatedBalanceFlow: IBalanceFlow = {
    ...balanceFlow,
    steps: balanceFlow.steps.map((step, index) => {
      if (index === stepIndex) {
        return { ...step, status: newStatus };
      }
      return step;
    }),
  };

  return updatedBalanceFlow;
}
export const updateBalanceStepsStatusByIndex = (
  steps: IBalanceStepDBData[],
  stepIndex: number,
  newStepStatus: IBalanceStepDBData['status'],
): IBalanceStepDBData[] =>
  steps.map((step, index) => (index === stepIndex ? { ...step, status: newStepStatus } : step));

export const updateBalanceStepsStatusByIndexes = (
  steps: IBalanceStepDBData[],
  updates: {
    index: number;
    status: IBalanceStepDBData['status'];
    doNotUpdateStatuses?: IBalanceStepDBData['status'][];
  }[],
): IBalanceStepDBData[] => {
  const updatesMap = new Map(
    updates.map(({ index, status, doNotUpdateStatuses = [] }) => [index, { status, doNotUpdateStatuses }]),
  );

  return steps.map((step, index) => {
    const update = updatesMap.get(index);
    if (update && !update.doNotUpdateStatuses.includes(step.status)) {
      return { ...step, status: update.status };
    }
    return step;
  });
};

export async function getExecutionInstructions(
  exposure: IMaterialExposure,
  routes: IRoute[],
): Promise<IBalanceInstruction[]> {
  const buyingInstructions = generatePositionInstructions(exposure, routes);
  if (buyingInstructions.length === 0) {
    return [];
  }
  const instrumentsArray = await getInstrumentsByIds(buyingInstructions.map((a) => a.instrumentId));
  const instrumentsMap = new Map(instrumentsArray.map((instrument) => [instrument.id, instrument]));

  const baseInstrumentIds = Array.from(new Set(instrumentsArray.map((item) => item.baseInstrumentId)));
  const baseInstrumentsArray = await getBaseInstrumentsByIds(baseInstrumentIds);
  const baseInstrumentMap = new Map(baseInstrumentsArray.map((baseInstrument) => [baseInstrument.id, baseInstrument]));

  return buyingInstructions
    .map(({ instrumentId, action, routeId, routeQuantity, positionType, multiplier, routePercent }) => {
      const instrument = instrumentsMap.get(instrumentId);
      if (!instrument) {
        console.warn(`Instrument with id ${instrumentId} not found`);
        return null;
      }

      const baseInstrument = baseInstrumentMap.get(instrument.baseInstrumentId);
      if (!baseInstrument) {
        console.warn(`Base Instrument with id ${instrument.baseInstrumentId} not found`);
        return null;
      }

      const instrumentDate = instrument.expiryDate
        .toDate()
        .toLocaleDateString('en-US', { month: 'short', year: '2-digit' });

      return {
        instrumentId,
        instrumentName: `${baseInstrument.displayName} ${instrumentDate}`,
        action,
        numberOfContracts: Math.floor(Math.abs(routeQuantity) / baseInstrument.amountPerContract),
        routeId,
        routeQuantity: Math.floor(Math.abs(routeQuantity)),
        positionType: positionType || 'long',
        expectedPrice: instrument.expectedPrice,
        lastPrice: instrument.lastPrice,
        expiryDate: instrument.expiryDate,
        quantityPerContract: baseInstrument.amountPerContract,
        maximumPrice: instrument.maximumPrice,
        status: 'pending',
        multiplier,
        routePercent,
      } as IBalanceInstruction;
    })
    .filter(
      (instruction) => instruction?.numberOfContracts && instruction.numberOfContracts > 0,
    ) as IBalanceInstruction[];
}

export const getBalanceTotalExpectedPrice = (instructions: IBalanceInstruction[]): number => {
  return instructions.reduce((sum, instruction) => {
    return sum + instruction.numberOfContracts * instruction.lastPrice * instruction.quantityPerContract;
  }, 0);
};

export const getBalanceTotalMaximumPrice = (instructions: IBalanceInstruction[]): number => {
  return instructions.reduce((sum, instruction) => {
    return sum + instruction.numberOfContracts * instruction.maximumPrice * instruction.quantityPerContract;
  }, 0);
};

// eslint-disable-next-line
const refinitivMonthCodes: { [key: string]: string } = {
  F: 'JAN',
  G: 'FEB',
  H: 'MAR',
  J: 'APR',
  K: 'MAY',
  M: 'JUN',
  N: 'JUL',
  Q: 'AUG',
  U: 'SEP',
  V: 'OCT',
  X: 'NOV',
  Z: 'DEC',
};

export const getFinancialPositionsSum = (balanceInstructions: IBalanceInstruction[], price: number): number => {
  //financial position sum is the routes quantity * ton-price , we dont care the price of the future contracts...
  return balanceInstructions
    .filter((item) => item.status !== 'pending')
    .reduce(
      (sum, { executedNumberOfContracts, multiplier, routePercent, positionType, quantityPerContract }) =>
        sum +
        (executedNumberOfContracts || 1) *
          quantityPerContract *
          (1 / multiplier) *
          routePercent *
          price *
          (positionType === 'long' ? 1 : -1),
      0,
    );
};

export const monthDiff = (dateFrom: Date, dateTo: Date): number => {
  const months = (dateTo.getFullYear() - dateFrom.getFullYear()) * 12 + (dateTo.getMonth() - dateFrom.getMonth()) || 0;

  return months <= 0 ? 1 : months + 1;
};

export const getMonths = (startingDate: Date, monthSpread: number, filterFrom: Date): MonthYear[] =>
  Array.from({ length: monthSpread })
    .map((_, i) => ({
      year: startingDate.getFullYear() + Math.floor((startingDate.getMonth() + i) / 12),
      month: ((startingDate.getMonth() + i) % 12) + 1,
    }))
    .filter(
      ({ year, month }) =>
        year > filterFrom.getFullYear() || (year === filterFrom.getFullYear() && month >= filterFrom.getMonth() + 1),
    );

export const calcExposureCalendar = (
  fromDate: Date,
  toDate: Date,
  quantity: number,
  type: 'long' | 'short',
): MonthYearValue[] => {
  const monthSpread = monthDiff(fromDate, toDate);

  const months = getMonths(fromDate, monthSpread, new Date());

  return months.map((date) => ({ date, value: quantity * (type === 'long' ? 1 : -1) }));
};

export const mapBalanceInstToPosition = (
  balanceInstructions: IBalanceInstruction[],
  { id, formulaId, accountId }: IBalanceFlow,
): IFinancialPosition[] => {
  const positions = balanceInstructions
    .filter(({ status, routeId, routeQuantity }) => status !== 'pending' && routeId !== '' && routeQuantity > 0)
    .map((i) => ({
      ...i,
      routeQuantity: (i.executedNumberOfContracts || 1) * i.quantityPerContract * (1 / i.multiplier) * i.routePercent,
    }))
    .map(
      (
        {
          expiryDate,
          positionType,
          routeId,
          instrumentId,
          instrumentName,
          routeQuantity,
          executedPrice,
          quantityPerContract,
          executedNumberOfContracts,
        },
        i,
      ) => {
        const expiryAsDate = expiryDate?.toDate ? expiryDate.toDate() : new Date(expiryDate as any);
        return {
          accountId,
          activityId: 'balance_' + id,
          createDate: new Date(),
          expiryDate: expiryAsDate,
          formulaId,
          positionType: positionType || ('long' as const),
          routeId,
          id: `${accountId}__${formulaId}_${i}_${new Date().getTime()}`,
          routeQuantity,
          exposureCalendar: calcExposureCalendar(new Date(), expiryAsDate, routeQuantity, positionType),
          contracts: [
            // why array??
            {
              instrumentId,
              name: instrumentName,
              avgBuyingPrice: executedPrice || 0,
              contractQuantity: executedNumberOfContracts || 0,
              buyingDate: new Date(),
              contractWeight: quantityPerContract,
              expiryDate: expiryAsDate,
            },
          ],
        };
      },
    );

  return positions;
};

export const sortByDate = (a: MonthYearValue, b: MonthYearValue): number =>
  a.date.year === b.date.year ? a.date.month - b.date.month : a.date.year - b.date.year;

export const getDateKey = ({ year, month }: MonthYear): string => `${year}${month}`;

export const getDateFromKey = (key: string): MonthYear => ({ year: +key.substring(0, 4), month: +key.substring(4) });

export const getPrevMonth = ({ year, month }: MonthYear): MonthYear =>
  month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };

export const getNextMonth = ({ year, month }: MonthYear): MonthYear =>
  month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };

export const makeDateMap = (monthValues: MonthYearValue[]): Record<string, number> =>
  monthValues.reduce(
    (res, { date, value }) => ({ ...res, [getDateKey(date)]: value + (res[getDateKey(date)] || 0) }),
    {} as Record<string, number>,
  );

export const toDate = (date: MonthYear): Date => new Date(date.year, date.month - 1, 10);

export const getInventoryCalendar = (value: number): MonthYearValue[] =>
  Array.from({ length: MAX_FUTURE_EXPOSURE })
    .map((_, i) => getDate(new Date(), i))
    .map((date) => ({ value, date: getMonthYear(date) }));

// export const genInventoryCalendar = (
//   exposureMap: Record<string, number>,
//   inventoryQuantity: number,
// ): MonthYearValue[] => {
//   const now = new Date();
//   let inventorySum = inventoryQuantity;
//   const invDatesExposure = Array.from({ length: MAX_FUTURE_EXPOSURE })
//     .map((_, i) => getMonthYear(getDate(now, i)))
//     .reduce((result, date) => {
//       const currExposure = exposureMap[getDateKey(date)] || 0;
//       if (currExposure < 0 && inventorySum > 0) {
//         inventorySum += currExposure;
//         const inventoryExp = Math.abs(inventorySum > 0 ? currExposure : currExposure - inventorySum);
//         return [...result, { date, value: inventoryExp }];
//       }
//       return result;
//     }, [] as MonthYearValue[]);

//   return invDatesExposure;
// };

// export const reduceInventoryExposure = (
//   exposureMap: Record<string, number>,
//   inventoryExposures: MonthYearValue[],
// ): Record<string, number> => {
//   inventoryExposures.forEach(({ date, value }) => {
//     exposureMap[getDateKey(date)] = exposureMap[getDateKey(date)] + value;
//   });
//   return exposureMap;
// };

export const year1LetterInstrums = ['LCC', 'CC', 'KC', 'LRC', 'LCO', 'LSU', 'CSR', 'NACFRJPSWM'];
export const futureMonths: Record<string, string> = {
  CC: 'HKNUZ',
  LCC: 'HKNUZ',
  LSU: 'HKQVZ',
  SB: 'HKNV',
  SFS: 'FHKNUX',
  CSR: 'FHKNUX',
  LRC: 'FHKNUX',
  KC: 'HKNUZ',
  KT: 'HKNUZ',
};

export const cocoaSymbols = ['RFVCBLEUD', 'RFVCLLEUD'];
export const cocoaMonths = ['FGH', 'FGH', 'FGH', 'JK', 'JK', 'MN', 'MN', 'QU', 'QU', 'VXZ', 'VXZ', 'VXZ'];
export const cocoaMonths_decode = { FGH: 3, JK: 5, MN: 7, QU: 9, VXZ: 12 };

export const getInstrument = (baseInstId: string, expiryDate: MonthYear): string => {
  if (cocoaSymbols.includes(baseInstId)) {
    const currYear = new Date().getFullYear();
    const years = expiryDate.year - currYear;
    const addition = years < 1 ? '' : years === 1 ? '1' : '';

    return baseInstId + (cocoaMonths[expiryDate.month - 1] || '') + addition;
  }

  return (
    baseInstId +
    getInstrumentKey(toDate(expiryDate), year1LetterInstrums.includes(baseInstId), futureMonths[baseInstId])
  );
};

export const isRouteAvailable = (route: IRoute, range: MonthYearValue): boolean => {
  const max = Math.max(...route.minMaxQuantity);
  const dateKey = `${range.date.year}-${range.date.month.toString().padStart(2, '0')}`;

  return range.value <= max; // !!(route.futures?.[dateKey] && route.futures?.[dateKey] > 0 && range.value <= max);
};

export interface IPositionRanges extends Record<string, number> {
  current: number;
  lastMonth: number;
  lastYear: number;
  firstValue: number;
  completed: number;
}

export const getBalanceArrowTypeLocation = (
  account: Account,
  formulaId: string,
  balanceFlowData: IBalanceFlow,
): { value: number; img: string } | undefined => {
  if (!account || !balanceFlowData.selectedBalanceMethod) return undefined;
  return {
    value: getBalanceMethodExposureLocation(
      balanceFlowData.selectedBalanceMethod.balanceType,
      getRiskTolerance(formulaId, account) || createEmptyRiskTolerance(),
      balanceFlowData.exposureBeforeBalance,
    ),
    img: BALANCE_METHOD_TO_IMAGE[balanceFlowData.selectedBalanceMethod.balanceType].arrowImage || '',
  };
};
